import React, { useState, useEffect } from 'react';
import { clientsApi } from '../services/api';
import { User } from '../contexts/AuthContext';
import { Building2, Edit3, Plus, Trash2, MapPin, DollarSign, Clock, Eye, EyeOff, Save, X, Users } from 'lucide-react';

interface Branch {
  id: number;
  nombre: string;
  direccion?: string;
  activa: boolean;
  tiempoEstimadoHoras?: number;
  precio_normal?: number;
  precio_urgente?: number;
  precio_prioritaria?: number;
  comisionOperador?: number;
  montoFijoOperador?: number;
  tipoComision?: 'porcentaje' | 'fijo';
  dvr_id?: string;
  dvr_password?: string;
  instrucciones_op?: string;
}

interface Client {
  id: number;
  nombre: string;
  email: string;
  telefono?: string;
  direccion?: string;
  contactoPrincipal: string;
  estado: string;
  // Valores por defecto del cliente (para herencia)
  costoAuditoria?: number;
  tiempoEstimado?: string; // Formato HH:MM
  comisionOperador?: number;
  montoFijoOperador?: number;
  tipoComision?: 'porcentaje' | 'fijo' | 'hibrido';
  branches: Branch[];
}

interface ClientBranchManagerProps {
  user: User;
}

const ClientBranchManager: React.FC<ClientBranchManagerProps> = ({ user }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());
  const [editingBranch, setEditingBranch] = useState<{ clientId: number; branchId?: number; branch?: Partial<Branch> } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll();
      if (response.data.success) {
        setClients(response.data.data);
      }
    } catch (error: any) {
      console.error('Error cargando clientes:', error);
      setError('Error cargando clientes');
    } finally {
      setLoading(false);
    }
  };

  const toggleClientExpansion = (clientId: number) => {
    setExpandedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  const handleEditBranch = (clientId: number, branch?: Branch) => {
    // Obtener datos del cliente padre para herencia
    const client = clients.find(c => c.id === clientId);

    setEditingBranch({
      clientId,
      branchId: branch?.id,
      branch: branch ? { ...branch } : {
        nombre: '',
        direccion: '',
        activa: true,
        // Heredar tiempo del cliente (convertir de HH:MM a horas decimales)
        tiempoEstimadoHoras: client?.tiempoEstimado ?
          (() => {
            const [horas, minutos] = client.tiempoEstimado.split(':').map(Number);
            return horas + (minutos / 60);
          })() : 2, // default 2 horas
        // Heredar precios del cliente
        precio_normal: client?.costoAuditoria || 0,
        precio_urgente: client?.costoAuditoria ? client.costoAuditoria * 1.5 : 0,
        precio_prioritaria: client?.costoAuditoria ? client.costoAuditoria * 1.25 : 0,
        // Heredar comisiones del cliente
        comisionOperador: client?.comisionOperador || 0,
        montoFijoOperador: client?.montoFijoOperador || 0,
        tipoComision: client?.tipoComision || 'porcentaje'
      }
    });
  };

  const handleSaveBranch = async () => {
    console.log('[BRANCH] Botón clickeado - handleSaveBranch ejecutándose');
    if (!editingBranch) {
      console.log('[BRANCH] No hay editingBranch, saliendo');
      return;
    }

    try {
      const { clientId, branchId, branch } = editingBranch;

      console.log('[BRANCH DEBUG] Guardando sucursal:', { clientId, branchId, branch });

      if (branchId) {
        // Actualizar sucursal existente
        console.log('[BRANCH DEBUG] Actualizando sucursal existente');
        await clientsApi.updateBranch(clientId, branchId, branch!);
        setSuccess('Sucursal actualizada exitosamente');
      } else {
        // Crear nueva sucursal
        console.log('[BRANCH DEBUG] Creando nueva sucursal');
        const result = await clientsApi.createBranch(clientId, branch!);
        console.log('[BRANCH DEBUG] Resultado creación:', result);
        setSuccess('Sucursal creada exitosamente');
      }

      setEditingBranch(null);
      await loadClients(); // Recargar datos
    } catch (error: any) {
      console.error('[BRANCH ERROR] Error guardando sucursal:', error);
      console.error('[BRANCH ERROR] Response:', error.response?.data);
      setError(error.response?.data?.error || 'Error guardando sucursal');
    }
  };

  const filteredClients = clients.filter(client =>
    client.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.contactoPrincipal.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.branches.some(branch =>
      branch.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Funciones auxiliares para herencia
  const getInheritedValue = (branchValue: any, clientValue: any, defaultValue: any = null) => {
    return branchValue !== null && branchValue !== undefined && branchValue !== ''
      ? branchValue
      : (clientValue !== null && clientValue !== undefined && clientValue !== ''
        ? clientValue
        : defaultValue);
  };

  const formatTimeFromHours = (hours?: number) => {
    if (!hours) return '';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatTimeFromString = (timeString?: string) => {
    if (!timeString) return '';
    return timeString.replace(':', 'h ') + 'm';
  };

  const isValueInherited = (branchValue: any, clientValue: any) => {
    return (branchValue === null || branchValue === undefined || branchValue === '') &&
           (clientValue !== null && clientValue !== undefined && clientValue !== '');
  };

  const getTotalBranches = () => {
    return clients.reduce((total, client) => total + client.branches.length, 0);
  };

  const getActiveBranches = () => {
    return clients.reduce((total, client) =>
      total + client.branches.filter(branch => branch.activa).length, 0
    );
  };

  // Función para eliminar cliente
  const handleDeleteClient = async (clientId: number, clientName: string) => {
    if (!window.confirm(`¿Está seguro que desea eliminar el cliente "${clientName}" y todas sus sucursales? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setLoading(true);
      await clientsApi.delete(clientId);
      setClients(prev => prev.filter(client => client.id !== clientId));
      setSuccess(`Cliente "${clientName}" eliminado exitosamente`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error eliminando cliente:', error);
      setError(error.response?.data?.error || 'Error eliminando cliente');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar sucursal
  const handleDeleteBranch = async (clientId: number, branchId: number, branchName: string) => {
    if (!window.confirm(`¿Está seguro que desea eliminar la sucursal "${branchName}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setLoading(true);
      await clientsApi.deleteBranch(clientId, branchId);

      // Actualizar el estado local
      setClients(prev => prev.map(client =>
        client.id === clientId
          ? { ...client, branches: client.branches.filter(branch => branch.id !== branchId) }
          : client
      ));

      setSuccess(`Sucursal "${branchName}" eliminada exitosamente`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error eliminando sucursal:', error);
      setError(error.response?.data?.error || 'Error eliminando sucursal');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Cargando clientes y sucursales...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Building2 style={{ width: '2rem', height: '2rem', color: '#3b82f6' }} />
          Gestión de Clientes y Sucursales
        </h1>

        {/* Estadísticas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            backgroundColor: '#f3f4f6',
            padding: '1rem',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
              {clients.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Clientes</div>
          </div>
          <div style={{
            backgroundColor: '#f3f4f6',
            padding: '1rem',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#059669' }}>
              {getTotalBranches()}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Sucursales</div>
          </div>
          <div style={{
            backgroundColor: '#f3f4f6',
            padding: '1rem',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
              {getActiveBranches()}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Sucursales Activas</div>
          </div>
        </div>

        {/* Buscador */}
        <div style={{ marginBottom: '2rem' }}>
          <input
            type="text"
            placeholder="Buscar clientes o sucursales..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              fontSize: '1rem'
            }}
          />
        </div>
      </div>

      {/* Mensajes */}
      {success && (
        <div style={{
          backgroundColor: '#d1fae5',
          color: '#065f46',
          padding: '0.75rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          border: '1px solid #10b981'
        }}>
          {success}
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '0.75rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          border: '1px solid #dc2626'
        }}>
          {error}
        </div>
      )}

      {/* Lista de Clientes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {filteredClients.map(client => (
          <div key={client.id} style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            {/* Header del Cliente */}
            <div
              onClick={() => toggleClientExpansion(client.id)}
              style={{
                padding: '1.5rem',
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e5e7eb',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Building2 style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6' }} />
                  {client.nombre}
                </h3>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  <span>📧 {client.email}</span>
                  <span>👤 {client.contactoPrincipal}</span>
                  <span>🏢 {client.branches.length} sucursales</span>
                  <span style={{
                    color: client.estado === 'activo' ? '#059669' : '#dc2626',
                    fontWeight: '500'
                  }}>
                    ● {client.estado}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {user.role === 'admin' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClient(client.id, client.nombre);
                    }}
                    style={{
                      padding: '0.5rem',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                    title="Eliminar cliente y todas sus sucursales"
                  >
                    <Trash2 style={{ width: '1rem', height: '1rem' }} />
                    Eliminar Cliente
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditBranch(client.id);
                  }}
                  style={{
                    padding: '0.5rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                  title="Agregar sucursal"
                >
                  <Plus style={{ width: '1rem', height: '1rem' }} />
                  Nueva Sucursal
                </button>
                {expandedClients.has(client.id) ?
                  <EyeOff style={{ width: '1.25rem', height: '1.25rem', color: '#6b7280' }} /> :
                  <Eye style={{ width: '1.25rem', height: '1.25rem', color: '#6b7280' }} />
                }
              </div>
            </div>

            {/* Sucursales del Cliente */}
            {expandedClients.has(client.id) && (
              <div style={{ padding: '1.5rem' }}>
                {client.branches.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                    No hay sucursales registradas para este cliente
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                    gap: '1rem'
                  }}>
                    {client.branches.map(branch => (
                      <div key={branch.id} style={{
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        padding: '1rem'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                          <h4 style={{
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            🏪 {branch.nombre}
                            {!branch.activa && (
                              <span style={{
                                fontSize: '0.75rem',
                                backgroundColor: '#fee2e2',
                                color: '#991b1b',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem'
                              }}>
                                INACTIVA
                              </span>
                            )}
                          </h4>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {user.role === 'admin' && (
                              <button
                                onClick={() => handleDeleteBranch(client.id, branch.id, branch.nombre)}
                                style={{
                                  padding: '0.375rem',
                                  backgroundColor: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.375rem',
                                  cursor: 'pointer'
                                }}
                                title="Eliminar sucursal"
                              >
                                <Trash2 style={{ width: '1rem', height: '1rem' }} />
                              </button>
                            )}
                            <button
                              onClick={() => handleEditBranch(client.id, branch)}
                              style={{
                                padding: '0.375rem',
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.375rem',
                                cursor: 'pointer'
                              }}
                              title="Editar sucursal"
                            >
                              <Edit3 style={{ width: '1rem', height: '1rem' }} />
                            </button>
                          </div>
                        </div>

                        <div style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: '1.5' }}>
                          {branch.direccion && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              <MapPin style={{ width: '0.875rem', height: '0.875rem' }} />
                              {branch.direccion}
                            </div>
                          )}

                          {/* Tiempo estimado - Mostrar herencia */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <Clock style={{ width: '0.875rem', height: '0.875rem' }} />
                            <span>
                              Tiempo estimado: {
                                branch.tiempoEstimadoHoras
                                  ? formatTimeFromHours(branch.tiempoEstimadoHoras)
                                  : formatTimeFromString(client.tiempoEstimado) || '4h 0m'
                              }
                              {isValueInherited(branch.tiempoEstimadoHoras, client.tiempoEstimado) && (
                                <span style={{
                                  color: '#3b82f6',
                                  fontSize: '0.75rem',
                                  marginLeft: '0.25rem',
                                  fontStyle: 'italic'
                                }}>
                                  (heredado del cliente)
                                </span>
                              )}
                            </span>
                          </div>

                          {/* Precios - Mostrar herencia */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <DollarSign style={{ width: '0.875rem', height: '0.875rem' }} />
                            <span>
                              Normal: ${getInheritedValue(branch.precio_normal, client.costoAuditoria, 100)} |
                              Urgente: ${getInheritedValue(branch.precio_urgente, client.costoAuditoria ? client.costoAuditoria * 2 : null, 200)} |
                              Prioritaria: ${getInheritedValue(branch.precio_prioritaria, client.costoAuditoria ? client.costoAuditoria * 1.5 : null, 150)}
                              {(!branch.precio_normal && client.costoAuditoria) && (
                                <span style={{
                                  color: '#3b82f6',
                                  fontSize: '0.75rem',
                                  marginLeft: '0.25rem',
                                  fontStyle: 'italic'
                                }}>
                                  (heredado)
                                </span>
                              )}
                            </span>
                          </div>

                          {/* Comisiones - Mostrar herencia */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <Users style={{ width: '0.875rem', height: '0.875rem' }} />
                            <span>
                              {(() => {
                                const tipoComision = getInheritedValue(branch.tipoComision, client.tipoComision, 'fijo');
                                const comisionPorcentaje = getInheritedValue(branch.comisionOperador, client.comisionOperador, 0);
                                const montoFijo = getInheritedValue(branch.montoFijoOperador, client.montoFijoOperador, 100);

                                if (tipoComision === 'porcentaje') {
                                  return `Comisión: ${comisionPorcentaje}%`;
                                } else {
                                  return `Monto fijo: $${montoFijo}`;
                                }
                              })()}
                              {(!branch.tipoComision && client.tipoComision) && (
                                <span style={{
                                  color: '#3b82f6',
                                  fontSize: '0.75rem',
                                  marginLeft: '0.25rem',
                                  fontStyle: 'italic'
                                }}>
                                  (heredado)
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal de Edición */}
      {editingBranch && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              {editingBranch.branchId ? 'Editar Sucursal' : 'Nueva Sucursal'}
            </h3>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {/* Datos básicos */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8fafc',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#1e40af', fontWeight: '600' }}>
                  📍 Datos Básicos de la Sucursal
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Nombre de la Sucursal *
                    </label>
                    <input
                      type="text"
                      value={editingBranch.branch?.nombre || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, nombre: e.target.value }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="Nombre de la sucursal"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Dirección
                    </label>
                    <input
                      type="text"
                      value={editingBranch.branch?.direccion || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, direccion: e.target.value }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="Dirección completa"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={editingBranch.branch?.activa ?? true}
                    onChange={(e) => setEditingBranch(prev => prev ? {
                      ...prev,
                      branch: { ...prev.branch, activa: e.target.checked }
                    } : null)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  <label style={{ fontWeight: '500' }}>
                    Sucursal activa
                  </label>
                </div>
              </div>

              {/* Sistema DVR */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#fef3c7',
                borderRadius: '0.5rem',
                border: '1px solid #f59e0b'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#d97706', fontWeight: '600' }}>
                  📹 Sistema DVR
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      ID del DVR
                    </label>
                    <input
                      type="text"
                      value={editingBranch.branch?.dvr_id || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, dvr_id: e.target.value }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="ID de acceso al DVR"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Contraseña del DVR
                    </label>
                    <input
                      type="text"
                      value={editingBranch.branch?.dvr_password || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, dvr_password: e.target.value }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="Contraseña del DVR"
                    />
                  </div>
                </div>
              </div>

              {/* Tiempo estimado */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#f0f9ff',
                borderRadius: '0.5rem',
                border: '1px solid #0ea5e9'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#0284c7', fontWeight: '600' }}>
                  ⏱️ Tiempo de Auditoría
                </h4>

                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Tiempo Estimado (horas)
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                      (dejar vacío para heredar del cliente)
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editingBranch.branch?.tiempoEstimadoHoras || ''}
                    onChange={(e) => setEditingBranch(prev => prev ? {
                      ...prev,
                      branch: { ...prev.branch, tiempoEstimadoHoras: parseFloat(e.target.value) || undefined }
                    } : null)}
                    style={{
                      width: '200px',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem'
                    }}
                    placeholder="ej: 4.5"
                  />
                </div>
              </div>

              {/* Precios */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '0.5rem',
                border: '1px solid #10b981'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#059669', fontWeight: '600' }}>
                  💰 Precios de Auditoría
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                    (dejar vacío para heredar del cliente)
                  </span>
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Precio Normal ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingBranch.branch?.precio_normal || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, precio_normal: parseFloat(e.target.value) || undefined }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="100"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Precio Urgente ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingBranch.branch?.precio_urgente || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, precio_urgente: parseFloat(e.target.value) || undefined }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="200"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Precio Prioritaria ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingBranch.branch?.precio_prioritaria || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, precio_prioritaria: parseFloat(e.target.value) || undefined }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="150"
                    />
                  </div>
                </div>
              </div>

              {/* Comisión del Operador */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#fdf4ff',
                borderRadius: '0.5rem',
                border: '1px solid #a855f7'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#9333ea', fontWeight: '600' }}>
                  👥 Comisión del Operador
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                    (dejar vacío para heredar del cliente)
                  </span>
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Tipo de Comisión
                    </label>
                    <select
                      value={editingBranch.branch?.tipoComision || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, tipoComision: e.target.value as 'porcentaje' | 'fijo' }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                    >
                      <option value="">Heredar del cliente</option>
                      <option value="porcentaje">Porcentaje</option>
                      <option value="fijo">Monto Fijo</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Comisión (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={editingBranch.branch?.comisionOperador || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, comisionOperador: parseFloat(e.target.value) || undefined }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="10"
                      disabled={editingBranch.branch?.tipoComision === 'fijo'}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                      Monto Fijo ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingBranch.branch?.montoFijoOperador || ''}
                      onChange={(e) => setEditingBranch(prev => prev ? {
                        ...prev,
                        branch: { ...prev.branch, montoFijoOperador: parseFloat(e.target.value) || undefined }
                      } : null)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}
                      placeholder="100"
                      disabled={editingBranch.branch?.tipoComision === 'porcentaje'}
                    />
                  </div>
                </div>
              </div>

              {/* Instrucciones para Operadores */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#fffbeb',
                borderRadius: '0.5rem',
                border: '1px solid #f59e0b'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#d97706', fontWeight: '600' }}>
                  📋 Instrucciones para Operadores
                </h4>

                <div>
                  <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
                    Instrucciones específicas
                  </label>
                  <textarea
                    value={editingBranch.branch?.instrucciones_op || ''}
                    onChange={(e) => setEditingBranch(prev => prev ? {
                      ...prev,
                      branch: { ...prev.branch, instrucciones_op: e.target.value }
                    } : null)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      minHeight: '100px',
                      resize: 'vertical'
                    }}
                    placeholder="Describe el layout, procedimientos específicos, puntos importantes que debe revisar el operador..."
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setEditingBranch(null);
                  setError('');
                  setSuccess('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <X style={{ width: '1rem', height: '1rem' }} />
                Cancelar
              </button>
              <button
                onClick={handleSaveBranch}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Save style={{ width: '1rem', height: '1rem' }} />
                {editingBranch.branchId ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientBranchManager;